import classNames from 'classnames'
import { useCallback, useContext, useEffect, useRef, useState } from 'react'

import { IForm } from 'core-components/classes/models/IForm'
import { ResizeContext } from 'core-components/context/resize'
import { getTextWidth } from 'core-components/helpers/getTextWidth'
import { Tooltip } from 'core-components/library/tooltip'

import { addDragMultiSelect } from './helpers/addDragMultiSelect'
import { BaseTemplate } from './itemsTemplate/BaseTemplate'
import { IBaseTemplate } from './itemsTemplate/models/IBaseTemplate'
import { IImmutable, IItemsLine, IRowState } from './models/IItemsLine'

import styles from './ItemsLine.module.scss'

export const ItemsLine = ({
  className,
  value,
  onDeleteValue,
  compact,
  onChange,
  onDataLoad,
  caption,
  multilineText,
  useDrag,
  moreButton,
  disabled,
  beforeRemoveHandler,
  itemTemplate,
}: IItemsLine) => {
  const rootRef = useRef<HTMLDivElement>(null)
  const sizeRef = useRef<HTMLDivElement>(null)
  const rowStateRef = useRef<IRowState>({ dragged: false, moreButtonFocused: false })
  const immutableRef = useRef<IImmutable>({})
  const [dragged, setDragged] = useState<boolean>(false)
  const [visibleItems, setVisibleItems] = useState<IForm[]>([])
  const [moreVisible, setMoreVisible] = useState<boolean>(false)
  const [removeElement, setRemoveElement] = useState<IForm | null>(null)

  const [sizeVersion] = useContext(ResizeContext)
  const getPlaceholderText = () => {
    const hiddenItems = value.length - visibleItems.length

    if (visibleItems.length === 0) {
      return `${hiddenItems} items`
    }

    return `and ${hiddenItems} more`
  }

  const getTooltipText = () => {
    if (disabled) {
      return value
        .map((el) => {
          const text = typeof el[caption] === 'string' ? el[caption] : el
          if (typeof text === 'string') {
            return text
          }
          return ''
        })
        .filter(Boolean)
        .join(', ')
    }

    return 'Show all selected items'
  }

  const removeButtonHandler = useCallback(
    async (el: IForm) => {
      setRemoveElement(el)
      const beforeRemoveResult = beforeRemoveHandler ? await beforeRemoveHandler(el) : true
      setRemoveElement(null)
      if (beforeRemoveResult !== false) {
        onDeleteValue(el)
      }
    },
    [beforeRemoveHandler, onDeleteValue]
  )

  useEffect(() => {
    if (!sizeRef.current || !moreButton?.visible || value.length === 1) {
      onDataLoad && onDataLoad(value)
      setMoreVisible(false)
      return setVisibleItems(value)
    }
    const availableWidth = sizeRef.current.clientWidth * 0.66
    const arrow = 18 // 14 width + 4 left spacing
    const padding = 16 // 8 left and right spacing
    const rightMargin = 4

    let sumWidth = 0

    const newVisible = value.filter((el) => {
      const text = typeof el[caption] === 'string' ? el[caption] : el
      // if an element is dynamically deleted from the data, it will have an invalid value
      if (typeof text === 'string') {
        const size = getTextWidth(text, 14, 400) + arrow + padding + rightMargin

        if (sumWidth + size < availableWidth) {
          sumWidth += size
          return true
        }
      }
      return false
    })

    const hasMoreButton = newVisible.length !== value.length
    setMoreVisible(hasMoreButton)
    if (hasMoreButton) {
      if (sumWidth + 100 > availableWidth) {
        newVisible.pop()
      }
    }

    onDataLoad && onDataLoad(newVisible)
    setVisibleItems(newVisible)
  }, [value, sizeVersion])

  useEffect(() => {
    if (rowStateRef.current.moreButtonFocused && !moreVisible) {
      rootRef.current?.focus()
    }
  }, [moreVisible])

  return (
    <div
      ref={rootRef}
      tabIndex={1}
      className={classNames(
        styles.root,
        className || '',
        dragged ? styles['root--dragged'] : '',
        disabled ? styles.disabled : ''
      )}
      data-testid={'itemsLineRoot'}
    >
      <div
        data-testid={'itemsLineContainer'}
        ref={sizeRef}
        className={'content-invisible'}
        style={{ position: 'absolute', left: 0, top: 0, right: 0 }}
      ></div>
      {visibleItems.map((el, index: number) => {
        let dragConf = {}
        if (useDrag) {
          dragConf = addDragMultiSelect(
            immutableRef.current,
            rowStateRef.current,
            el,
            index,
            visibleItems,
            setDragged,
            onChange,
            caption
          )
        }

        const itemTemplateProps: IBaseTemplate<any> = {
          item: el,
          caption,
          onDeleteValue: removeButtonHandler,
          multilineText: !!multilineText,
          useDrag: !!useDrag,
          compact: !!compact,
          disabled: !!disabled,
          isRemoving: el === removeElement,
          __testId: 'BaseTemplateItemInLine',
        }

        return (
          <div
            id={`destination-drag-${index}`}
            key={index}
            className={styles.itemContainer}
            data-testid={'itemsLineDragItem'}
            {...dragConf}
          >
            {useDrag && (
              <div
                className={styles.destination}
                onDragOver={() => {
                  if (immutableRef.current.past !== 'before') {
                    immutableRef.current.past = 'before'
                  }
                }}
                onDragLeave={() => {
                  immutableRef.current.past = ''
                }}
                data-testid={'itemsLineDragDestinationBefore'}
              />
            )}
            {itemTemplate ? (
              itemTemplate(itemTemplateProps)
            ) : (
              <BaseTemplate {...itemTemplateProps} />
            )}
            {index === visibleItems.length - 1 && useDrag && (
              <div
                className={styles.destination}
                onDragOver={() => {
                  if (immutableRef.current.past !== 'after') {
                    immutableRef.current.past = 'after'
                  }
                }}
                onDragLeave={() => {
                  immutableRef.current.past = ''
                }}
                data-testid={'itemsLineDragDestinationAfter'}
              />
            )}
          </div>
        )
      })}

      {moreVisible ? (
        <Tooltip
          tipContent={() => {
            return <div>{getTooltipText()}</div>
          }}
        >
          <div
            data-testid={'showMoreButton'}
            className={styles.moreButton}
            tabIndex={10}
            onFocus={() => {
              rowStateRef.current.moreButtonFocused = true
              if (moreButton?.onFocus) {
                moreButton.onFocus()
              }
            }}
            onBlur={() => {
              rowStateRef.current.moreButtonFocused = false
              if (moreButton?.onBlur) {
                moreButton.onBlur()
              }
            }}
          >
            {getPlaceholderText()}
          </div>
        </Tooltip>
      ) : null}
    </div>
  )
}
